import React, { useState } from 'react';
import { View, Text, Image, StyleSheet, TouchableOpacity, Modal, TouchableWithoutFeedback, Platform } from 'react-native';
import { Video, ResizeMode } from 'expo-av';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { RecentPost } from '../screens/dashboard/RecentPostsCard';
import { PostCommentsModal } from './PostCommentsModal';
import { PostInsightsModal } from './PostInsightsModal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';

const STATUS_COLOR: Record<string, string> = {
  published: '#10B981',
  scheduled: '#F59E0B',
  draft: colors.textLight as string,
  failed: '#EF4444',
};

// Fixed Facebook-feed-style media box — a stable, predictable aspect ratio
// (a single fixed box for every post, rather than each post reflowing the
// card to its own image's dimensions), kept compact/short like Facebook's
// own feed thumbnails.
const MEDIA_ASPECT_RATIO = 16 / 9;

function timeAgo(dateStr: string, justNow: string): string {
  const date = new Date(dateStr);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return justNow;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export interface PostCardProps {
  post: RecentPost;
  onPress?: (post: RecentPost) => void;
  /** Caps body text to a couple of lines — used in previews (slider, list rows);
   * the full detail view should pass false to show the entire message. */
  compact?: boolean;
  /** Needed to open the comments/likers modal (Graph API calls are scoped to
   * a page's access token) — omit to hide that affordance entirely (e.g. in
   * contexts that don't have a pageId on hand). */
  pageId?: string;
  /** Suppresses the card's own comments/likers modal — used where a caller
   * (e.g. PostsListScreen's right pane) shows PostCommentsPanel inline
   * instead, so tapping the comments/likes count doesn't pop a redundant modal
   * on top of an already-visible panel. */
  disableCommentsModal?: boolean;
  /** Shows the "…" button (post actions). */
  onMore?: (post: RecentPost) => void;
}

/** Rich Facebook-style post card: page avatar/name, relative time, full message,
 * a fixed-size media box (image/video) that opens fullscreen on tap — just like
 * Facebook's own feed/lightbox — and a real insights strip (reach/reactions/
 * comments/shares) sourced from the backend's Graph API-backed stats. Tapping
 * the comments or likes count opens the comments/likers viewer. */
export const PostCard: React.FC<PostCardProps> = ({ post, onPress, compact = true, pageId: pageIdProp, disableCommentsModal, onMore }) => {
  const { t } = useLocale();
  const pageId = pageIdProp || post.pageId;
  const styles = useThemedStyles(getStyles);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showPostInsights, setShowPostInsights] = useState(false);
  const statusColor = STATUS_COLOR[post.status];
  const live = post.status === 'published';
  const dateLabel = post.publishedAt || post.scheduledFor || post.createdAt;
  const hasMedia = !!(post.mediaUrls && post.mediaUrls.length > 0);
  const insights = post.insights;

  const Wrapper: any = onPress ? TouchableOpacity : View;

  return (
    <>
      <Wrapper style={styles.card} onPress={onPress ? () => onPress(post) : undefined} activeOpacity={0.85}>
        <View style={styles.header}>
          {post.pageAvatar ? (
            <Image source={{ uri: post.pageAvatar }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, styles.avatarFallback]}>
              <Ionicons name="logo-facebook" size={18} color="#1877F2" />
            </View>
          )}
          <View style={styles.headerText}>
            <Text style={styles.pageName} numberOfLines={1}>{post.pageName || t('posts.facebookPage')}</Text>
            <View style={styles.timeRow}>
              <Text style={styles.time}>
                {post.status === 'scheduled' && post.scheduledFor
                  ? t('posts.scheduledFor', { date: new Date(post.scheduledFor).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) })
                  : timeAgo(dateLabel, t('posts.justNow'))}
              </Text>
              {statusColor && !live && (
                <>
                  <Text style={styles.timeDot}>·</Text>
                  <Text style={[styles.statusInline, { color: statusColor }]}>{t(`posts.status.${post.status}` as any)}</Text>
                </>
              )}
            </View>
          </View>
          {onMore && (
            <TouchableOpacity
              onPress={(e: any) => { e?.stopPropagation?.(); onMore(post); }}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel={t('posts.actions')}
            >
              <Ionicons name="ellipsis-horizontal" size={18} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
        </View>

        {post.status === 'failed' && (
          <View style={styles.failedBox}>
            <Ionicons name="alert-circle" size={16} color="#EF4444" />
            <Text style={styles.failedText} numberOfLines={compact ? 2 : undefined}>
              {post.errorCode === 'missing_permissions' ? t('posts.error.permissions') : (post.errorMessage || t('posts.error.generic'))}
            </Text>
          </View>
        )}

        {!!post.message && (
          <Text style={styles.message} numberOfLines={compact ? 3 : undefined}>{post.message}</Text>
        )}

        {hasMedia && (
          <TouchableOpacity
            style={styles.mediaContainer}
            activeOpacity={0.9}
            onPress={() => setIsFullscreen(true)}
          >
            {post.mediaType === 'video' ? (
              <Video
                source={{ uri: post.mediaUrls![0] }}
                style={styles.media}
                resizeMode={ResizeMode.COVER}
                useNativeControls={false}
                isLooping={false}
              />
            ) : (
              <Image source={{ uri: post.mediaUrls![0] }} style={styles.media} resizeMode="cover" />
            )}
            <View style={styles.expandBadge}>
              <Ionicons name={post.mediaType === 'video' ? 'play' : 'expand'} size={14} color="#FFFFFF" />
            </View>
            {post.mediaUrls!.length > 1 && (
              <View style={styles.mediaCountBadge}>
                <Ionicons name="images-outline" size={12} color="#FFFFFF" />
                <Text style={styles.mediaCountText}>+{post.mediaUrls!.length - 1}</Text>
              </View>
            )}
          </TouchableOpacity>
        )}

        {!!post.linkUrl && !hasMedia && (
          <View style={styles.linkRow}>
            <Ionicons name="link-outline" size={14} color={colors.text.secondary as string} />
            <Text style={styles.linkText} numberOfLines={1}>{post.linkUrl}</Text>
          </View>
        )}

        {live && (
        <View style={styles.insightsRow}>
          <View style={styles.insightItem}>
            <Ionicons name="eye-outline" size={14} color={colors.text.secondary as string} />
            <Text style={styles.insightText}>{insights?.reach ?? 0}</Text>
          </View>
          <TouchableOpacity
            style={styles.insightItem}
            onPress={pageId && !disableCommentsModal ? () => setShowComments(true) : undefined}
            disabled={!pageId || disableCommentsModal}
          >
            <Ionicons name="heart-outline" size={14} color={colors.text.secondary as string} />
            <Text style={styles.insightText}>{insights?.reactions ?? 0}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.insightItem}
            onPress={pageId && !disableCommentsModal ? () => setShowComments(true) : undefined}
            disabled={!pageId || disableCommentsModal}
          >
            <Ionicons name="chatbubble-outline" size={14} color={colors.text.secondary as string} />
            <Text style={styles.insightText}>{insights?.comments ?? 0}</Text>
          </TouchableOpacity>
          <View style={styles.insightItem}>
            <Ionicons name="share-social-outline" size={14} color={colors.text.secondary as string} />
            <Text style={styles.insightText}>{insights?.shares ?? 0}</Text>
          </View>
          {pageId && post.status === 'published' && (
            <TouchableOpacity
              style={styles.insightItem}
              onPress={(e: any) => { e?.stopPropagation?.(); setShowPostInsights(true); }}
            >
              <Ionicons name="stats-chart-outline" size={14} color={colors.brand.primary as string} />
              <Text style={[styles.insightText, { color: colors.brand.primary as string }]}>{t('posts.insights')}</Text>
            </TouchableOpacity>
          )}
        </View>
        )}
      </Wrapper>

      {pageId && !disableCommentsModal && (
        <PostCommentsModal
          visible={showComments}
          post={post}
          pageId={pageId}
          onClose={() => setShowComments(false)}
        />
      )}

      {pageId && (
        <PostInsightsModal
          visible={showPostInsights}
          post={post}
          pageId={pageId}
          onClose={() => setShowPostInsights(false)}
        />
      )}

      {hasMedia && (
        <Modal visible={isFullscreen} transparent animationType="fade" onRequestClose={() => setIsFullscreen(false)}>
          <TouchableWithoutFeedback onPress={() => setIsFullscreen(false)}>
            <View style={styles.fullscreenOverlay}>
              <TouchableOpacity
                style={styles.fullscreenCloseButton}
                onPress={() => setIsFullscreen(false)}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Ionicons name="close" size={28} color="#FFFFFF" />
              </TouchableOpacity>
              <TouchableWithoutFeedback>
                {post.mediaType === 'video' ? (
                  <Video
                    source={{ uri: post.mediaUrls![0] }}
                    style={styles.fullscreenMedia}
                    resizeMode={ResizeMode.CONTAIN}
                    useNativeControls
                    shouldPlay
                  />
                ) : (
                  <Image source={{ uri: post.mediaUrls![0] }} style={styles.fullscreenMedia} resizeMode="contain" />
                )}
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </>
  );
};

const getStyles = () => StyleSheet.create({
  failedBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 6, padding: 8, borderRadius: 8, backgroundColor: 'rgba(239,68,68,0.08)', marginTop: 8 },
  failedText: { flex: 1, fontSize: typography.fontSize.sm, color: '#EF4444' },
  linkRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 },
  linkText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.brand.primary },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.md,
    gap: designTokens.spacing.sm,
    ...designTokens.shadows.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  avatarFallback: {
    backgroundColor: colors.background.base,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  pageName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  time: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  timeDot: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  statusInline: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  message: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    lineHeight: 20,
  },
  mediaContainer: {
    width: '100%',
    aspectRatio: MEDIA_ASPECT_RATIO,
    maxHeight: 220,
    borderRadius: designTokens.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: colors.background.base,
  },
  media: {
    width: '100%',
    height: '100%',
  },
  expandBadge: {
    position: 'absolute',
    bottom: designTokens.spacing.xs,
    right: designTokens.spacing.xs,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaCountBadge: {
    position: 'absolute',
    top: designTokens.spacing.xs,
    right: designTokens.spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
  },
  mediaCountText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
    color: '#FFFFFF',
  },
  insightsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.md,
    paddingTop: designTokens.spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  insightText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  fullscreenOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullscreenCloseButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 56 : 24,
    right: 20,
    zIndex: 1,
  },
  fullscreenMedia: {
    width: '100%',
    height: '80%',
  },
});
